import { Link, useLocation } from "react-router-dom";
import { useAuthStore } from "@/stores/auth.store.js";
import { Icon } from "@/components/ui/icons.js";
import { NAV_GROUPS, type NavEntry } from "./nav.js";

interface SidebarProps {
  collapsed: boolean;
  mobileOpen: boolean;
  onNavigate: () => void;
}

function NavItem({ entry, active, onNavigate }: { entry: NavEntry; active: boolean; onNavigate: () => void }) {
  if (entry.planned) {
    return (
      <div className="nav-item nav-item--disabled" aria-disabled="true" title="Noch nicht aktiv">
        <span className="nav-item__icon"><Icon name={entry.icon} /></span>
        <span className="nav-item__label">{entry.label}</span>
        <span className="nav-item__badge">geplant</span>
      </div>
    );
  }
  // "im Projekt"-Module: klickbarer Verweis auf die Projektliste, KEIN eigener
  // Active-State (sie sind kein eigener Ort, sondern Tabs im Projekt). Das Flag
  // unterdrückt weiterhin den falschen Active-State + liefert den Tooltip; das
  // frühere "im Projekt"-Badge ist entfernt — diese Module SIND live (E2E-
  // verifiziert), ein Badge ließ sie fälschlich unfertig wirken.
  const showActive = active && !entry.inProject;
  return (
    <Link
      to={entry.path}
      className={`nav-item${showActive ? " nav-item--active" : ""}`}
      onClick={onNavigate}
      {...(entry.inProject ? { title: "Im Projekt verfügbar — Projekt öffnen" } : {})}
    >
      <span className="nav-item__icon"><Icon name={entry.icon} /></span>
      <span className="nav-item__label">{entry.label}</span>
    </Link>
  );
}

export function Sidebar({ collapsed, mobileOpen, onNavigate }: SidebarProps) {
  const { user } = useAuthStore();
  const location = useLocation();
  const role = user?.role ?? "user";

  const classes = [
    "sidebar",
    collapsed ? "sidebar--collapsed" : "",
    mobileOpen ? "sidebar--mobile-open" : "",
  ].filter(Boolean).join(" ");

  return (
    <nav className={classes} aria-label="Hauptnavigation">
      <div className="sidebar__brand">
        <span className="sidebar__logo" style={{ color: "var(--color-brand)" }}>
          <Icon name="bolt" size={28} />
        </span>
        <span className="sidebar__brand-text">
          <span className="sidebar__brand-name">Energy<b>OS</b></span>
          <span className="sidebar__brand-sub">Expert Platform</span>
        </span>
      </div>

      <div className="sidebar__nav">
        {NAV_GROUPS.map((group) => {
          const items = group.items.filter((it) => {
            // Installateure sehen ausschließlich die Schnellkalkulation.
            if (role === "installer") return it.path === "/kalkulation";
            return !it.adminOnly || role === "admin";
          });
          if (items.length === 0) return null;
          return (
            <div key={group.label}>
              <div className="sidebar__group-label">{group.label}</div>
              {items.map((entry) => {
                const active =
                  location.pathname === entry.path ||
                  location.pathname.startsWith(entry.path + "/");
                {/* key=label: mehrere "im Projekt"-Einträge teilen sich /projects */}
                return <NavItem key={entry.label} entry={entry} active={active} onNavigate={onNavigate} />;
              })}
            </div>
          );
        })}
      </div>

      <div className="sidebar__footer">
        <div className="sidebar__user">
          <span className="sidebar__avatar">{role.charAt(0).toUpperCase()}</span>
          <span className="sidebar__user-meta">
            <span className="sidebar__user-name">{role.charAt(0).toUpperCase() + role.slice(1)}</span>
            <span className="sidebar__user-role">Tenant {user?.tenantId.slice(0, 8) ?? "—"}…</span>
          </span>
        </div>
      </div>
    </nav>
  );
}
